
import React, { useCallback, useMemo, useState } from 'react'
import { useParams, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'

import { CERTIFICATE_STATUSES } from 'constants/statuses'

import { getActionLogById } from 'store/action-log/actions'
import { startScanSession } from 'store/scan-session/actions'
import {
  IValidationScanSessionMessageCallbackParams,
  IValidationStartScanSessionCallbackParams,
} from 'store/scan-session/types'
import { getOwner, getErrorMessage, isBrandHeldCertificate, isCertificateInTransferState, getTransferDisabledMessage, canSplitCertificateGroup, TRANSFER_PERMISSION_TOOLTIP, CUSTODY_RECEIVE_PERMISSION_TOOLTIP, isTransferAvailable, isActivateAvailable, isCancelTransferAvailable, isReplaceTagAvailable, isDeleteAvailable, isViewLiveAvailable } from 'utils'

import type { CertificateAdmin, CertificateStatus } from 'types'

import { DEFAULT_COMPANY_ID } from 'config'
import { useSnackbar } from 'contexts'

import { useCertificate, useCertificateMutations, useProductsLastImport, useCanAccess, useDidMountEffect, useIsDemoCompany, usePrimaryInstallation, useReferrerState, type UsePaginationEvent } from 'hooks'
import { useCertificateArchiveMutations, useCertificateScans, useConvertDemoCertificate, useReleaseHeldReplacement, useCertificateGroup, useCertificateGroupUnlink, useAcknowledgeInvalidScans, useOpenCustodyHops, useCustodyMutations } from 'hooks/api'
import { Box, Stack, Typography, Divider, Tooltip, useTheme } from '@mui/material'
import { Button, Modal, Tabs, TabPanel, Alert, isInvalidScan, getScanLocationLabel } from '@repo/ui'
import { isInternalPermission, resolveActivationTrigger } from '@repo/types'
import {
  Header,
  ActionDropdown,
  type ActionDropdownItem,
  TransferCertificateModal,
  CancelTransferModal,
  ReplaceTagModal,
  DeleteCertificateModal,
  SplitGroupModal,
  CreateCertificateGroupModal,
  CertificateGroupRoleIcon,
  ConfirmSettingModal,
  ConvertDemoRowModal,
  getConvertCertificateRefusalMessage,
  ActivateCertificateModal,
  SendToLocationModal,
  Table,
  CertificatePreviewDrawer,
} from 'components'
import type { CertificateGroupRole } from 'components/display/CertificateGroupMemberPreview'
import { BadgeCheckIcon, FlaskConicalOffIcon, TrashIcon, ReplaceIcon, TagIcon, EyeIcon, SplitIcon, UnlinkIcon, MergeIcon, ArrowLeftRightIcon, ArrowUpRightIcon, ShieldAlert, X, XIcon, InfoIcon, ExternalLinkIcon, ArchiveIcon, PackageIcon, PackageCheckIcon, MapPinPlusIcon, MapPinCheckIcon } from 'lucide-react'
/*
  §21 — `PackageIcon` / `PackageCheckIcon` stay imported ON PURPOSE: they still carry
  the tag-REPLACEMENT shipment meaning below ("Mark as received" at the held-replacement
  menu row, and the held-replacement banner's icon). The custody pair — Send to location
  / Confirm arrival — moved OFF them to map pins, because those two actions are about a
  PLACE, and reusing the package glyph for both features made two unrelated flows look
  like one. Keep the pin pair identical to CertificatesPage's row kebab.
*/
import { Label } from 'recharts'
import { ROUTES } from 'routes'


// import { useDispatch, useSelector } from 'store/hooks'

import { Product, Provenance, Scans, ActivityTab } from './components'
// Direct file import, NOT through `./components`. Routing it through that barrel produced a
// `ReferenceError: InvalidScanPopover is not defined` at runtime on a full reload (it
// survived HMR, which is what makes this worth a comment rather than a silent fix): the
// barrel re-exports the whole tab set, and pulling this page's own header dependency back
// out of it closes an initialisation cycle in which the binding is still in TDZ when the
// header renders. Same hazard, and same remedy, as the `pages` barrel note below.
import { InvalidScanPopover } from './components/InvalidScanPopover'
import { ExampleKindBadge } from 'components/display/ExampleKindBadge'
// VRG-491 — the certificate's two integrity signals, shared with the certificates table so
// the glyph, the colours and the wording cannot drift between the list and the detail page.
// (The invalid-scan half is reached through `InvalidScanPopover` below, which renders the
// shared `InvalidScanIndicator` as its trigger — the page does not import the indicator
// directly any more.)
import { TamperBadge } from 'components/display/TamperBadge'
import { CertificatePageSkeleton } from './CertificatePageSkeleton'
// Direct file import (not the `pages` barrel) — this file is itself part of
// that barrel, so importing back from it is a circular import that resolves
// NotFoundPage to `undefined` at module-init and silently blanks the page.
import { NotFoundPage } from 'pages/other/NotFoundPage/NotFoundPage'


interface CertificatePageProps {
  tab?: string
}

export const CertificatePage = (props: CertificatePageProps) => {
  const { tab } = props
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme();

  const { open: notify } = useSnackbar()

  const { data, isLoading, refetch } = useCertificate({ certificateId: id as string })
  /*
    VRG-528 — the header's invalid-scan tally comes straight off the detail payload now.
    It used to be a SECOND request (`useCertificateInvalidScans`, VRG-491), and that hook
    existed only because this page loaded through the OptionalAuth public endpoint, whose
    non-owner branch is the anonymous-scanner projection and carries no count. The page
    now loads through `GET /certificates/admin/:id`, the same projection the certificates
    TABLE renders — so the header and the row share one server-side computation by
    construction, which is what the extra request was buying, at the cost of a round trip.
  */
  const certificate = data
  const isEmpty = data === null
  const { isHasAccess: isCanTransfer } = useCanAccess({ action: 'transfer_certificate' })
  // VERA-931 / VRG-528 — confirming arrival has its OWN permission. `transfer_certificate`
  // gates DISPATCH ("Send to location"); receipt is the billable moment and is done by
  // shop/logistics staff, so it gates separately. company_logistics holds this WITHOUT
  // holding transfer_certificate, so the two must not be conflated here.
  const { isHasAccess: isCanReceiveCustody } = useCanAccess({ action: 'custody_receive' })
  const { isHasAccess: isCanManageGroups } = useCanAccess({ action: 'manage_certificate_groups' })
  // VRG-268: the dedicated permission for repointing a certificate at a new physical tag.
  // Mirrors the `@Permissions(CertificateReplaceTag)` guard on the API — the server is the
  // boundary; this only decides whether the control is usable.
  const { isHasAccess: canReplaceTag } = useCanAccess({ action: 'certificate_replace_tag' })
  /**
   * VRG-388 — whether this certificate has a tag at all, which decides whether the one tag
   * control reads "Replace tag" or "Add tag" (§46: label follows record state).
   *
   * Read from the RAW columns rather than "does it resolve to a tag row", matching the server's
   * own guard: a `tag_uid` naming no `tags` row is still an identifier, and treating it as
   * absent would offer Add on a certificate the server will refuse to add to. VRG-231: the
   * `e_tag_id` term that used to be in this chain is gone — it was an alias of `tag_uid`
   * carrying the identical value, so dropping it changes nothing about the outcome.
   */
  const certificateHasTag = Boolean(
    certificate?.tag_uid ?? certificate?.tag_id,
  )
  // §24 (working-conventions): Activate and Delete are their own permissions, not
  // folded into `manage_certificate` — the API's guards agree
  // (certificates.admin.controller.ts activate uses
  // @Permissions(PermissionName.CertificateActivate); delete uses
  // @Permissions(PermissionName.CertificateDelete)). Both are platform_admin ONLY
  // for now — a deliberate, explicitly provisional product decision (VERA-455).
  const { isHasAccess: canActivateCertificate } = useCanAccess({ action: 'certificate_activate' })
  const { isHasAccess: canDeleteCertificate } = useCanAccess({ action: 'certificate_delete' })
  /**
   * VRG-491 — acknowledging invalid scans is its OWN permission, not `manage_certificate`.
   * Deciding that suspected counterfeit activity has been dealt with is a different
   * capability from editing a certificate. Seeded to exactly the roles that hold
   * `manage_certificate` (platform_admin, platform_manager, company_admin,
   * company_team_member) — that is the permission actually gating certificate CREATION
   * (POST /certificates), which is the capability this most closely tracks.
   *
   * §46: a company_admin CAN grant this, so the menu row is DISABLED with a tooltip naming
   * the permission, never hidden. (Contrast `certificate_delete` above, which no company
   * role can ever hold and is therefore hidden.) The server's
   * `@Permissions(CertificateAcknowledgeInvalidScans)` guard is the real boundary; this only
   * decides whether the control is usable.
   */
  const { isHasAccess: canAcknowledgeInvalidScans } = useCanAccess({
    action: 'certificate_acknowledge_invalid_scans',
  })
  const [openAcknowledgeModal, setOpenAcknowledgeModal] = useState(false)
  /*
   * VRG-836 — "Convert to real" (`POST /demo-data/certificates/:id/convert`). ⚠️ BILLABLE:
   * the server clears `example_kind` AND records the activation, so it lands on the next
   * invoice. GATED BEFORE APPLYING (§43) — the kebab only stages this flag; nothing is
   * called until the modal's confirm, so a cancel cannot leave the badge having lied.
   *
   * Its own state, not reused from another modal: confirming into the wrong handler here
   * would spend the brand's money.
   */
  const [openConvertDemoModal, setOpenConvertDemoModal] = useState(false)
  const isDemoCompany = useIsDemoCompany()
  const {
    convertDemoCertificateAsync,
    isConvertingDemoCertificate,
    convertDemoCertificateError,
  } = useConvertDemoCertificate()

  const handleConfirmConvertDemo = async () => {
    if (!certificate) return
    try {
      const result = await convertDemoCertificateAsync(certificate.id)
      /*
       * `converted: true, metered: false` is a FREE REAL CERTIFICATE — the server's own
       * docblock calls it the one result a caller must surface as a fault rather than as
       * success. Announced and the modal left open, because closing it would report success.
       */
      if (result.converted && !result.metered) {
        notify({
          type: 'error',
          title:
            'This certificate was converted but the activation was not recorded. ' +
            'Contact support before relying on it.',
        })
        return
      }
      if (!result.converted) {
        notify({type: 'error', title: getConvertCertificateRefusalMessage(result.refusal)})
        return
      }
      setOpenConvertDemoModal(false)
      // Visible-action success — the Demo badge leaves the title — so no success toast.
    } catch {
      // Left open deliberately.
    }
  }

  const { acknowledgeInvalidScans, isAcknowledging } = useAcknowledgeInvalidScans({
    onSuccess: () => {
      // No success toast, deliberately. The effect is VISIBLE — the indicator disappears
      // from the header and this row leaves the menu — and the toast policy is that a
      // visible action does not also announce itself. A toast here would be a second
      // notification of something the user is already watching happen.
      setOpenAcknowledgeModal(false)
    },
    onError: (error) => {
      // Failure IS invisible (the indicator simply stays), so it must be announced. Object
      // form — `notify` takes a NotificationProps, not (message, severity).
      notify({ type: 'error', title: getErrorMessage(error) || 'Could not acknowledge invalid scans' })
    },
  })
  /**
   * How many invalid scans are OUTSTANDING — the server already excludes any older than the
   * last acknowledgement, so this is 0 on an acknowledged certificate and non-zero again the
   * moment a newer invalid scan lands. The menu row keys off this, so it re-appears on its
   * own without the page knowing anything about acknowledgement state.
   */
  const unacknowledgedInvalidScans = certificate?.invalid_scan_count ?? 0
  // VRG-273: `certificate_delete` is now internal-scoped (packages/types permission-groups)
  // and the write-side allowlist rejects granting it to any company role — so no company
  // admin can ever hold it. §27: disable+tooltip is for actions the user has a path to
  // request; where there is none, ever, the rule is to HIDE instead. `isInternalUser` gates
  // that hide; `isInternalPermission` is read generically (not hardcoded to this one action)
  // so the next internal-scoped permission gets the same treatment for free.
  const { isHasAccess: isInternalUser } = useCanAccess({ role: 'platform_admin' })
  const { data: primaryInstallation, isLoading: isPrimaryLoading, isError: isPrimaryError, refetch: refetchPrimaryInstallation } = usePrimaryInstallation()

  // VRG-234/235 — certificate groups. Role is derivable synchronously from fields
  // already on `certificate` (no need to wait on the group fetch below): a CHILD
  // has `parent_certificate_id` set; a PARENT has `group_divisibility` set but no
  // parent id; a standalone certificate has neither, so `groupRole` is undefined
  // and no group UI (title glyph, Children/Parent/Related sections) renders at all.
  const groupRole: CertificateGroupRole | undefined = certificate?.parent_certificate_id
    ? 'child'
    : certificate?.group_divisibility
      ? 'parent'
      : undefined
  const { data: groupData, isLoading: isGroupLoading, refetch: refetchGroup } = useCertificateGroup(
    { certificateId: id as string },
    { enabled: Boolean(groupRole) },
  )
  const [openSplitGroupModal, setOpenSplitGroupModal] = useState(false)
  const [openCreateGroupModal, setOpenCreateGroupModal] = useState(false)
  // "Details" (group-member kebab) is a detail→detail link — see useReferrerState's
  // documented convention for why an explicit label is passed here.
  const groupMemberReferrer = useReferrerState('Certificate')

  const handleOpenSplitGroupModal = () => setOpenSplitGroupModal(true)
  const handleCloseSplitGroupModal = () => setOpenSplitGroupModal(false)
  const handleSplitGroupSuccess = () => {
    refetch()
    refetchGroup()
  }

  // VRG-231 review item 7 — "Create group" entry point. Wiring the full multi-select
  // grouping flow (picking members, a divisibility choice, creating the tagless
  // parent) is VRG-337-Group.Existing.Certificates — this only opens a placeholder
  // so the affordance exists and is discoverable ahead of that work.
  // TODO(VRG-337): replace this placeholder with the real "group certificates" modal.
  const handleOpenCreateGroupModal = () => setOpenCreateGroupModal(true)
  const handleCloseCreateGroupModal = () => setOpenCreateGroupModal(false)

  // VERA-415 / VERA-908: the "Suspicious activity detected" banner names WHERE the
  // invalid scan happened, so it's actionable rather than just an alarm. `has_invalid_scans`
  // is a lifetime flag (any invalid scan ever), not necessarily the most recent scan, so
  // the location can't be read off `certificate.last_scan` — fetch the certificate's scans
  // (same hook + params the Scans tab uses, so it shares that cached fetch when the tab is
  // also open) and find the most recent invalid one. Only fetched when the banner can
  // actually show, to avoid an extra round-trip on every certificate detail view.
  const { data: certificateScansData } = useCertificateScans(
    { certificateId: id as string, limit: 50, offset: 0 },
    { enabled: Boolean(certificate?.has_invalid_scans) },
  )
  const latestInvalidScan = useMemo(() => {
    const scans = certificateScansData?.data ?? []
    return [...scans]
      .filter((scan: any) => isInvalidScan(scan))
      .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
  }, [certificateScansData])
  const invalidScanLocation = latestInvalidScan
    ? getScanLocationLabel((latestInvalidScan as any).metadata)
    : null

  // State for modals
  const [selectedCertificate, setSelectedCertificate] = useState<CertificateAdmin | null>(null)
  const [openPreview, setOpenPreview] = useState(false)
  const [openTransferModal, setOpenTransferModal] = useState(false)
  const [openCancelTransferModal, setOpenCancelTransferModal] = useState(false)
  const [openReplaceTagModal, setOpenReplaceTagModal] = useState(false)

  /*
    VERA-931 — the custody hop. `useOpenCustodyHops` is the dispatch-age report's
    query, reused here rather than adding a per-certificate endpoint: a company has
    few open hops by construction (they close on arrival), and sharing the key means
    sending an item from this page updates the report without a second round trip.
  */
  const [openSendToLocation, setOpenSendToLocation] = useState(false)
  const [openConfirmArrival, setOpenConfirmArrival] = useState(false)
  const { hops: openHops } = useOpenCustodyHops()
  const openHopForCertificate = openHops.find(
    hop => hop.certificate_id === (id as string),
  )
  const custodyState = useCustodyMutations({
    onDispatched: () => {
      setOpenSendToLocation(false)
      notify({ type: 'success', title: 'Sent to location' })
      refetch?.()
    },
    onReceived: result => {
      /*
        The response says whether receipt ACTIVATED the certificate, and the toast
        must too. "Arrival confirmed" alone would hide a charge that just happened —
        under AT_LOCATION this is the billable moment, and the person confirming a
        delivery is entitled to know that their click cost money.
      */
      notify({
        type: 'success',
        title: result.activated
          ? 'Arrival confirmed — certificate activated'
          : 'Arrival confirmed',
      })
      refetch?.()
    },
    onError: error => notify({ type: 'error', title: getErrorMessage(error) }),
  })

  /*
    Resolved through the SHARED resolver, so the gate's wording and the server's
    billing decision cannot disagree — the same reason the row itself uses it.
  */
  const resolvedActivatesOnArrival =
    resolveActivationTrigger({
      certificate: certificate?.activation_trigger ?? null,
      product: certificate?.product?.activation_trigger ?? null,
    }).trigger === 'AT_LOCATION'

  const handleConfirmArrival = useCallback(() => {
    if (!openHopForCertificate) return
    setOpenConfirmArrival(false)
    custodyState.confirmReceipt({
      custodyEventId: openHopForCertificate.id,
      certificateId: id as string,
      // An explicit accept from the platform, not a scan. `scan_claimed` is reserved
      // for a real tag read at the store — claiming it here would overstate the
      // evidence behind a billable event.
      method: 'transferred',
    })
  }, [openHopForCertificate, custodyState, id])
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openActivateModal, setOpenActivateModal] = useState(false)
  const [openArchiveModal, setOpenArchiveModal] = useState(false)
  // VRG-275 — confirms the brand-side release of a HELD replacement (the operational
  // escape hatch; see the ActionDropdown item below and useReleaseHeldReplacement).
  const [openReleaseHoldModal, setOpenReleaseHoldModal] = useState(false)
  const [showSuspiciousAlert, setShowSuspiciousAlert] = useState(true)
  const [pendingStatusAction, setPendingStatusAction] = useState<'activate' | null>(null)

  const { releaseHeldReplacement, isReleasingHold } = useReleaseHeldReplacement({
    onSuccess: async () => {
      await refetch()
      setOpenReleaseHoldModal(false)
      setSelectedCertificate(null)
      notify({ type: 'success', title: 'Replacement tag activated' })
    },
    onError: error => {
      notify({ type: 'error', title: getErrorMessage(error) })
    },
  })

  const [{ activate: activateCertificateStatus }, mutationState] = useCertificateMutations({
    onSuccess: async () => {
      if (!pendingStatusAction) return

      await refetch()


      setPendingStatusAction(null)
    },
    onError: error => {
      if (!pendingStatusAction) return

      notify({
        type: 'error',
        title: getErrorMessage(error),
      })

      setPendingStatusAction(null)
    },
  })

  // if (isLoading) {
  //   return <Spinner />
  // }


  const handlePreviewCertificate = async (certificate: CertificateAdmin) => {
    // If still loading, try to wait for it or refetch
    if (isPrimaryLoading || !primaryInstallation) {
      try {
        const { data } = await refetchPrimaryInstallation()
        if (!data?.installation_url) {
          notify({
            type: 'error',
            title: 'Primary installation not found',
          })
          return
        }
        const baseUrl = data.installation_url
        const separator = baseUrl.includes('?') ? '&' : '?'
        const url = `${baseUrl}${separator}certificate_id=${encodeURIComponent(certificate.id)}`
        window.open(url, '_blank', 'noopener,noreferrer')
        return
      } catch (error) {
        notify({
          type: 'error',
          title: 'Failed to load primary installation',
        })
        return
      }
    }

    if (isPrimaryError || !primaryInstallation?.installation_url) {
      console.error('Primary installation error or missing', { 
        isPrimaryError, 
        primaryInstallation,
        installationUrl: primaryInstallation?.installation_url 
      })
      notify({
        type: 'error',
        title: 'Primary installation not found',
      })
      return
    }

    const baseUrl = primaryInstallation.installation_url
    const separator = baseUrl.includes('?') ? '&' : '?'
    const url = `${baseUrl}${separator}certificate_id=${encodeURIComponent(certificate.id)}`

    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleTransferCertificate = (certificate: CertificateAdmin) => {
    // Prevent transferring certificates already in transfer state
    if (isCertificateInTransferState(certificate.status)) {
      const message = getTransferDisabledMessage(certificate.status) || 'This certificate is already being transferred'
      notify({
        type: 'error',
        title: 'Cannot transfer certificate',
        message: message,
      })
      return
    }
    
    setSelectedCertificate(certificate)
    setOpenTransferModal(true)
  }

  const handleDeleteCertificate = (certificate: CertificateAdmin) => {
    const hasOwner = certificate.customer_id || certificate.customer

    if (hasOwner) {
      notify({ type: 'error', title: 'Cannot delete certificate', message: 'Certificates with owners cannot be deleted' })
      return
    }

    if (certificate.status !== 'inactive') {
      notify({ type: 'error', title: 'Cannot delete certificate', message: 'Only inactive certificates can be deleted' })
      return
    }

    setSelectedCertificate(certificate)
    setOpenDeleteModal(true)
  }

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
    setSelectedCertificate(null)
  }

  // After delete succeeds the certificate no longer exists — leave the detail page.
  const handleDeleted = () => {
    setOpenDeleteModal(false)
    setSelectedCertificate(null)
    navigate(ROUTES.CERTIFICATES)
  }

  const { archiveCertificate, unarchiveCertificate, isArchiving, isUnarchiving } = useCertificateArchiveMutations({
    onSuccess: async () => {
      await refetch()
      setOpenArchiveModal(false)
    },
    onError: (error) => {
      notify({ type: 'error', title: getErrorMessage(error) })
    },
  })

  const handleToggleArchive = (cert: CertificateAdmin) => {
    // `archived_at`, NOT `archived`. See types/Certificate.ts — the boolean was
    // declared but no API path has ever emitted it, so this read was always undefined
    // and archiving was the only reachable branch.
    if (cert.archived_at) {
      unarchiveCertificate(cert.id)
    } else {
      setOpenArchiveModal(true)
    }
  }

  const handleConfirmArchive = () => {
    if (certificate) {
      archiveCertificate(certificate.id)
    }
  }

  // VRG-231 review round 2 item 5 — "Remove from group" (member-row kebab).
  // REVERSES the Aug-7 "Split from group" rename: the parent's own action stays
  // "Split group" (it dissolves the whole thing), but a single member's own
  // removal reads "Remove from group" — "split" on a per-member row implied
  // dissolving the whole group, which is misleading for the common case (a
  // partial removal from a group of >= 3 that stays a live group afterward).
  // Severs this member's link to the group only; the certificate itself is
  // never deleted, hence Unlink (not Trash) per §38. Rejected server-side for
  // indivisible groups (see UnlinkCertificatesFromGroupUseCase) — the row is
  // gated the same way "Split group" already is, so it's never offered
  // somewhere the API would 400.
  const { unlinkCertificateGroup, isUnlinking } = useCertificateGroupUnlink({
    onSuccess: () => {
      notify({ type: 'success', title: 'Removed from group' })
      refetch()
      refetchGroup()
      setPendingRemoveFromGroupRow(null)
    },
    onError: (error) => {
      notify({ type: 'error', title: getErrorMessage(error) })
    },
  })

  // VRG-231 review round 2 item 5 — a removal that would leave the group with
  // exactly ONE remaining member is not a partial split: UnlinkCertificatesFromGroupUseCase
  // auto-expands "a group of 1 is not a group" into a FULL DISSOLVE, taking every
  // other member down too — which surprised a real user who removed one member of
  // a 2-member group and lost both (see the task file's Verify Pass history). So this
  // one case is gated behind a confirm that names the consequence up front (§43);
  // every other removal (>= 3 members before removal, so >= 2 survive) proceeds
  // immediately, exactly like before, since it stays a live group afterward.
  // `groupData.children` is "all of the parent's children" regardless of whether
  // THIS page is the parent or a child (see GetCertificateGroupResponseDto), so its
  // length is the group's full, current member count from any viewing angle.
  const [pendingRemoveFromGroupRow, setPendingRemoveFromGroupRow] = useState<CertificateAdmin | null>(null)

  const handleRemoveFromGroup = (row: CertificateAdmin) => {
    // VRG-231 review round 3 — a parent with ONE child is now a valid group, so the
    // server only dissolves when the removal leaves ZERO children. Was `<= 2` (i.e.
    // "one would remain"), matching the old server rule. `children` excludes the
    // parent, so "this is the last child" is a count of 1.
    //
    // This gate decides whether to CONFIRM or just fire, so it must track the server
    // exactly in both directions: too loose and it warns about a dissolve that will
    // not happen, too tight and a real dissolve goes through with no confirmation at
    // all — the second being the one that loses a certificate silently.
    const willFullyDissolve = (groupData?.children?.length ?? 0) <= 1
    if (willFullyDissolve) {
      setPendingRemoveFromGroupRow(row)
    } else {
      unlinkCertificateGroup({ certificateId: row.id })
    }
  }

  const handleCloseRemoveFromGroupModal = () => {
    setPendingRemoveFromGroupRow(null)
  }

  const handleConfirmRemoveFromGroup = () => {
    if (pendingRemoveFromGroupRow) {
      unlinkCertificateGroup({ certificateId: pendingRemoveFromGroupRow.id })
    }
  }

  const handleCloseArchiveModal = () => {
    setOpenArchiveModal(false)
  }

  // VRG-275 — the brand-side escape hatch for a HELD replacement (see the
  // ActionDropdown item + the held-replacement Alert above the tabs). Gated BEFORE
  // applying (§43): the tag does not go live until the confirm below fires, and the
  // consequence — the brand vouching for the owner, not the owner confirming — is
  // named there rather than assumed from a bare "Mark as received" click.
  const handleOpenReleaseHoldModal = (cert: CertificateAdmin) => {
    setSelectedCertificate(cert)
    setOpenReleaseHoldModal(true)
  }

  const handleConfirmReleaseHold = () => {
    if (selectedCertificate) {
      releaseHeldReplacement(selectedCertificate.id)
    }
  }

  const handleCloseReleaseHoldModal = () => {
    setOpenReleaseHoldModal(false)
    setSelectedCertificate(null)
  }

  const handleCloseTransferModal = () => {
    setSelectedCertificate(null)
    setOpenTransferModal(false)
  }

  const handleCancelTransferCertificate = (certificate: CertificateAdmin) => {
    setOpenCancelTransferModal(true)
    setSelectedCertificate(certificate)
  }

  const handleCloseCancelTransferModal = () => {
    setOpenCancelTransferModal(false)
    setSelectedCertificate(null)
  }

  const handleReplaceTagModal = (certificate: CertificateAdmin) => {
    setSelectedCertificate(certificate)
    setOpenReplaceTagModal(true)
  }

  const handleCloseReplaceTagModal = () => {
    setOpenReplaceTagModal(false)
    setSelectedCertificate(null)
  }

  const handleActivateCertificate = (certificate: CertificateAdmin) => {
    setSelectedCertificate(certificate)
    setOpenActivateModal(true)
  }

  const handleCloseActivateModal = () => {
    setOpenActivateModal(false)
    setSelectedCertificate(null)
  }

  const handleConfirmActivate = () => {
    if (selectedCertificate) {
      setPendingStatusAction('activate')
      activateCertificateStatus({ certificateId: selectedCertificate.id })
      setOpenActivateModal(false)
    }
  }



  // VRG-231 review item 1 — the header's own top-level actions (View / Activate /
  // Archive) are rendered as BUTTONS again (see the Header below), matching the
  // established platform convention (CompanyPage: a Stack of Buttons + an overflow
  // kebab) instead of collapsing everything into a single "Actions ▾" dropdown.
  // getActionItems now only builds the OVERFLOW kebab contents — the actions that
  // don't earn a permanent button: Transfer, Replace tag, Split group, Create
  // group, Delete.
  const getActionItems = useCallback(
    (row: CertificateAdmin | null): ActionDropdownItem[] => {
      if (!row) return []

      // ---------------------------------------------------------------------------------
      // Standing rule (2026-08-12, VERA-455): an action is unavailable for exactly one of
      // three reasons, and each gets a different treatment.
      //   1. PERMISSION — the user lacks the grant. VISIBLE + `disabled` + `tooltip` naming
      //      the permission. The disabled row is the only channel telling them the
      //      capability exists and that an admin can grant it — resolution is owned by
      //      someone else, asynchronously, so the item has to stay discoverable.
      //   2. RECORD STATE — the row's own data makes it impossible right now (status,
      //      archived, ownership, an in-flight transfer). HIDE it. Resolution here is owned
      //      by the user and is one action away — the app owes them the RESOLVING action
      //      (e.g. Cancel transfer), not a tombstone explaining the blocked one. A disabled
      //      item here is churn that never becomes a learned fact.
      //   3. IN-FLIGHT MUTATION — a request this menu triggered is still running
      //      (isActivating, isArchiving, isUnarchiving). Stays `disabled` + tooltip, NEVER
      //      hidden — hiding would make the item vanish under the cursor mid-click. This is
      //      transient UI state, not record state.
      // Permission never lives in `visible`; record state never lives in `disabled` (the
      // one deliberate exception is category 3, which is transient UI state, not record
      // state). See CertificatesPage.tsx for the same rule applied to the list + bulk bar.
      // ---------------------------------------------------------------------------------

      // VRG-268: replace is available at ANY status. It used to be hidden unless the
      // certificate was `inactive`, which excluded the case the feature exists for — a tag
      // damaged or lost on an item that has already been sold. An owned item is precisely when
      // a dead tag matters, so gating it out made the action available only when nobody needed
      // it. Access is now decided by permission, not by lifecycle state.
      // VRG-231 review round 2 item 12 — with ONE record-state exception: a group PARENT
      // is tagless by construction, so there is no tag to replace. Children keep it.
      const isGroupParentRow = groupRole === 'parent'
      const isVisibleEditTag = !isGroupParentRow
      // Category 2 (record state) — `inactive` is the ONLY status the endpoint accepts.
      // `transfer-certificate-to-owner.use-case.ts` 400s anything else: the four in-transfer
      // states with "already in a transfer state", everything else with 'should be in
      // "Inactive" status'. This read `=== 'active'` — the exact inverse — so every Transfer
      // the menu offered led to a 400 after submitting, and the one status that would have
      // worked never showed the action at all. That late failure is what §46 exists to
      // prevent, so the status lives in `visible`, never in `disabled`.
      //
      // Hiding creates an obligation (§46): the RESOLVING action must be reachable in the
      // state that hid this one. Mid-transfer -> `Cancel transfer` below returns the
      // certificate to `inactive`. `active` is not a blocker to resolve — it means an owner
      // has already claimed it, so it is not the brand's to hand out.
      // VRG-231 round 5 — predicate moved to utils/certificate: ONE definition per
      // action, shared by every kebab that offers it. Two of the four hand-written
      // copies had drifted; see that module's note.
      const isVisibleTransfer = isTransferAvailable(row.status)
      // Category 2 — Cancel transfer is the resolving action for the one transfer state it
      // covers. `isAdminCanCancel` reflects that only a store-initiated transfer can be
      // cancelled from here.
      const isVisibleCancelTransfer = isCancelTransferAvailable(row.status)
      // `'primary'` is the type `transfer-certificate-to-owner.use-case.ts` writes for a
      // brand-initiated transfer — the only kind a brand may call off. This compared against
      // `'store'`, which is not a member of the server's enum at all (see the TransferType
      // note in types/Certificate.ts), so it was constant false and this row never rendered.
      // That matters more now that Transfer correctly hides at `waiting_accept`: this IS the
      // resolving action §46 obliges us to leave reachable. An owner's own `sell`/`gift`
      // transfer stays excluded — it is not the brand's to cancel.
      const isAdminCanCancel = certificate?.transfer?.type === 'primary'
      // TODO(VERA-455 / TD-071): this predicate cannot mean what it is being asked to mean.
      // `DeactivateCertificateUseCase` sets status back to Inactive, so 'inactive' covers both
      // "never activated" and "was activated, billed, scanned, then deactivated" — and a hard
      // delete cascades away `scans` and `certificate_metering`. Needs an evidence-based check
      // (no metering row, no activation in the action log, no scans, no owner) enforced
      // SERVER-side, with archive as the default disposition. See VRG-274.
      //
      // VRG-273: visibility rule — `certificate_delete` is internal-scoped and no company
      // role can ever hold it (the API allowlist rejects granting it via a custom
      // role/override), so §27 says HIDE for non-internal users, not disable+tooltip (there
      // is no path to request access). An internal user who still lacks the grant keeps the
      // disabled+tooltip treatment below — their admin CAN grant it.
      // The shared delete predicate answers "is this record deletable"; the internal-
      // permission clause is a separate question (WHO may delete) and stays here —
      // it is a property of the viewer, not of the certificate.
      const isVisibleDelete =
        isDeleteAvailable(row) &&
        (isInternalUser || !isInternalPermission('certificate_delete'))
      const isVisibleActivate = isActivateAvailable(row.status)

      // The header renders View live / Preview / Replace tag / Activate inline; everything
      // below is what did not fit, and together the two are the parity set the row kebab on
      // CertificatesPage offers (minus "Details" — this page IS the details).
      //
      // ⚠️ THIS USED TO OPEN "NOTHING APPEARS BOTH AS A HEADER BUTTON AND IN THIS MENU",
      // and that invariant was already false when it was written. "View live" had been moved
      // out of the header to make room for Activate and re-added as an item HERE, while this
      // comment, the one over the Preview button and the one on Replace tag all went on
      // describing a header "View live" button that no longer existed — three statements of a
      // fact the file itself contradicted. View live is back in the header, the duplicate
      // here is gone, and those three comments are true again. Do not re-add it here.
      //
      // ⚠️ But do NOT restore the invariant as a blanket claim, because ACTIVATE is still
      // in both places: the header renders it as the primary button on exactly the statuses
      // where this menu also lists it (`isVisibleActivate`, the same predicate). Observed on
      // an inactive certificate — a header "Activate" plus a kebab of Transfer / Activate /
      // Create group / Archive. That duplication is argued for on the group-member menu below
      // (the VRG-231 note: a member row should not offer less than the same certificate's row
      // elsewhere); whether it is wanted HERE, where the header button is two inches away, is
      // a real question this change does not answer. Raising it rather than silently deleting
      // a third surface's action.
      return [
        /*
          VERA-931 — the HQ -> store custody hop, as TWO items that are never both
          visible. Which one shows is pure record state (category 2 above): a
          certificate is either in transit to a store or it is not, and the app owes
          the user the action that resolves the state it is actually in rather than a
          disabled tombstone for the other one.
        */
        {
          label: 'Send to location',
          onClick: () => setOpenSendToLocation(true),
          // Hidden while a hop is open — an item cannot be in transit to two shops,
          // and the API returns 409 for exactly this. Also hidden once the
          // certificate has left the brand: a customer's item is not the brand's
          // stock to move.
          visible: !openHopForCertificate && isBrandHeldCertificate(row),
          disabled: !isCanTransfer,
          /*
            TWO tooltips, because the row can be unavailable for one reason and
            unclear for another — the same shape "Assign to buyer" uses. Lacking the
            permission it names what to ask for (§46). Holding it, it says what the
            action DOES: "Send to location" does not convey that the item stays the
            brand's in transit, nor that nothing is charged until someone confirms
            arrival, and a menu row is the last place that can say so before the
            modal opens.
          */
          tooltip: !isCanTransfer
            ? TRANSFER_PERMISSION_TOOLTIP
            : 'Record that this item is on its way to one of your locations. It stays yours in transit, and nothing is charged until arrival is confirmed there.',
          icon: <MapPinPlusIcon strokeWidth={1} />,
        },
        {
          label: 'Confirm arrival',
          // Opens the GATE, not the action. Confirming arrival activates the
          // certificate under AT_LOCATION, and activation is the billed event — so it
          // takes the same ConfirmSettingModal every other activation change takes
          // (AC-11). It had none, which made the one irreversible step in the whole
          // custody flow the only one with no confirmation.
          onClick: () => setOpenConfirmArrival(true),
          visible: Boolean(openHopForCertificate),
          // Gated on `custody_receive`, NOT `transfer_certificate` — see the hook above.
          disabled: !isCanReceiveCustody || custodyState.isReceiving,
          // Third branch added: with the permission and no request in flight, say
          // what confirming COSTS. This is the billable moment under AT_LOCATION and
          // the one irreversible step in the custody flow, so the row should not be
          // the only place that stays silent about it.
          tooltip: !isCanReceiveCustody
            ? CUSTODY_RECEIVE_PERMISSION_TOOLTIP
            : custodyState.isReceiving
              ? 'Confirming…'
              : 'Record that the item reached the location. If this certificate activates at location, confirming is what starts the charge.',
          icon: <MapPinCheckIcon strokeWidth={1} />,
        },
        {
          label: 'Transfer',
          onClick: () => handleTransferCertificate(row),
          // Category 2 (record state) — see the note above.
          visible: isVisibleTransfer,
          // Category 1 (permission) only from here down — state is fully excluded via
          // `visible` above.
          disabled: !isCanTransfer,
          tooltip: !isCanTransfer
            ? TRANSFER_PERMISSION_TOOLTIP
            : undefined,
          icon: <ArrowLeftRightIcon strokeWidth={1} />
        },
        {
          label: 'Activate',
          onClick: () => handleActivateCertificate(row),
          // §24: gates on `certificate_activate` — see the file-top note. Two independent
          // disable reasons (category 1 permission + category 3 in-flight mutation), so the
          // tooltip must name whichever is actually active — never leave it silently disabled.
          visible: isVisibleActivate,
          disabled: !canActivateCertificate || mutationState.isActivating,
          tooltip: !canActivateCertificate
            ? "You need the 'Activate certificate' permission. Ask an admin to grant it."
            : mutationState.isActivating
              ? 'Activating…'
              : undefined,
          icon: <BadgeCheckIcon strokeWidth={1} />
        },
        {
          // Category 2 (record state): only offered while a store-initiated transfer is
          // genuinely pending acceptance. Severs a pending transfer rather than destroying a
          // record, so it takes the "remove"-style X glyph (§38), not Trash.
          label: 'Cancel transfer',
          onClick: () => handleCancelTransferCertificate(row),
          visible: isVisibleCancelTransfer && isAdminCanCancel,
          // Category 1 (permission) — cancelling a transfer is gated by the same permission
          // as starting one.
          disabled: !isCanTransfer,
          tooltip: !isCanTransfer
            ? TRANSFER_PERMISSION_TOOLTIP
            : undefined,
          icon: <XIcon strokeWidth={1} />,
        },
        {
          // VRG-235 — "Split group". Ungroup, not Trash: this destroys the group
          // RELATIONSHIP, not any certificate record — every member survives
          // (§38). VRG-231 review round 2 item 7 — NOT destructive: splitting
          // deletes no record, it only severs a relationship, so it doesn't earn
          // the red treatment (§32/§38).
          // VRG-231 review round 2 item 3 — SUPERSEDES the old "divisible only"
          // gate: an indivisible group's parent can ALSO reach Split group while
          // every member (parent + children) is still not-yet-live and unowned —
          // see `canSplitCertificateGroup` (utils/certificate.ts), which mirrors
          // the server's `assertIndivisibleGroupIsSplittable`. `groupData` is
          // already fetched above (enabled once `groupRole` is set), so this
          // needs no extra request.
          // VRG-231 fix G — ALSO hidden (record state, §46) once the group has
          // no remaining children (a parent left behind by an older split):
          // opening the modal on an empty list with a permanently-disabled
          // Split button is a dead end.
          label: 'Split group',
          onClick: handleOpenSplitGroupModal,
          visible:
            groupRole === 'parent' &&
            canSplitCertificateGroup(row.group_divisibility, groupData) &&
            isCanManageGroups &&
            Boolean(groupData?.children?.length),
          icon: <SplitIcon strokeWidth={1} />,
        },
        {
          // VRG-275 — the resolving action for a HELD replacement (Category 2, record
          // state): only offered while one actually exists on this certificate. This is
          // the OPERATIONAL escape hatch, not the normal path — the owner confirming
          // receipt themselves is — which is why it lives in the menu rather than beside
          // Replace tag in the header, and why the confirm modal below names what it
          // actually does.
          label: 'Mark as received',
          onClick: () => handleOpenReleaseHoldModal(row),
          visible: Boolean(row.held_replacement),
          // Category 1 (permission) — same grant as starting a replacement — plus
          // Category 3 (in-flight mutation).
          disabled: !canReplaceTag || isReleasingHold,
          tooltip: !canReplaceTag
            ? "You need the 'Replace tag' permission. Ask an admin to grant it."
            : isReleasingHold
              ? 'Marking as received…'
              : undefined,
          icon: <PackageCheckIcon strokeWidth={1} />,
        },
        /* VRG-491 — "Acknowledge invalid scans" USED TO BE A ROW HERE, AND ITS REMOVAL IS
           DELIBERATE. Do not re-add it as an obvious oversight, and do not read the gap as
           §46 losing an action: the action still exists, on `InvalidScanPopover`, opened by
           clicking the invalid-scan indicator in the page header. That is now the SOLE path
           to it, by explicit user decision (2026-08-31).

           The commit that built the popover kept this row on purpose, and the reasoning it
           gave was not wrong — it is the cost being knowingly paid. The indicator only
           reveals its clickability on hover, so at rest it still reads as a status badge
           rather than a control; the kebab was the fallback that made discoverability an
           enhancement rather than a single point of failure. The user was told this
           explicitly and accepted it, preferring exactly one path to the action over two.

           So if the popover's discoverability ever proves to be the problem this trade
           anticipated, the fix is to make the INDICATOR read as a control at rest — not to
           quietly restore a second entry point in a menu three surfaces away from the count
           it acts on, which is the arrangement the popover was built to replace. */
        // Cancel transfer is offered at `waiting_accept` only, and that is CORRECT — the
        // other three transfer states belong to the customer-to-customer flow and are
        // cancelled by the two customers, not the brand. Full reasoning lives once next to
        // TRANSFER_STATES in utils/certificate.ts.
        {
          // VRG-231 review item 7 — hidden entirely (not disabled) once the
          // certificate is already part of a group, same rationale as "Split
          // group" above: that's a property of the record, not something a
          // permission grant changes. When visible, gated on
          // manage_certificate_groups via disable+tooltip (§27 — an in-context
          // action, not a whole surface).
          label: 'Create group',
          onClick: handleOpenCreateGroupModal,
          visible: !groupRole,
          disabled: !isCanManageGroups,
          tooltip: !isCanManageGroups
            ? "You need the 'Manage Certificate Groups' permission. Ask an admin to grant it."
            : undefined,
          icon: <MergeIcon strokeWidth={1} />,
        },
        {
          /*
            VRG-836 — "Convert to real": clears this certificate's `demo` badge AND bills the
            activation. Mirrors the certificates LIST kebab exactly, item for item and in the
            same slot, because a list row and its own detail page offering different actions
            for the same record is the drift the kebab/detail parity rule exists to prevent.

            §73 — BEFORE Archive, which stays the last non-destructive item. Convert is not
            destructive (§32): it ends a classification rather than destroying a record, so no
            red and no `destructive`.

            §46 — RECORD STATE hides it: only a `demo` row can convert, never inside a demo
            COMPANY (the server refuses `company-is-still-a-demo` there, and every row is
            Vera's anyway — which is why `ExampleKindBadge` is suppressed there too), and never
            without a tag, because metering has nothing to write without one. The cost is named
            by the confirm, not by this row — see `ConvertDemoRowModal`.
          */
          label: 'Convert to real',
          onClick: () => setOpenConvertDemoModal(true),
          visible:
            row.example_kind === 'demo' &&
            !isDemoCompany &&
            // `??`, not `||`, matching `certificateHasTag`'s documented raw-column reading
            // above: these are RAW columns, so an empty-string `tag_uid` is still a value the
            // server's own guard reads as present, and `||` would fall through to `tag_id`
            // and answer a different question than the endpoint does. Read off `row` rather
            // than the `certificateHasTag` closure const because every other predicate in
            // this callback reads its parameter, and mixing the two is how the two readings
            // drift apart.
            Boolean(row.tag_uid ?? row.tag_id),
          // Category 3 (in-flight) — disabled with a reason, never hidden.
          disabled: isConvertingDemoCertificate,
          tooltip: isConvertingDemoCertificate ? 'Converting…' : undefined,
          icon: <FlaskConicalOffIcon strokeWidth={1} />,
        },
        {
          /*
            ARCHIVE IS ALWAYS THE LAST NON-DESTRUCTIVE ITEM, immediately above Delete.

            It used to sit above "Create group", which put a lifecycle action in the
            middle of the ordinary ones and left the menu ending on a workflow item.
            Archive is the end of a record's working life, so it belongs at the end of
            the list — and grouping it with Delete is what makes the divider mean
            something: everything below the line changes whether the record is in play
            at all. The certificates LIST already ordered it this way; only the detail
            page disagreed, which is precisely the drift a header-vs-row parity rule
            exists to prevent.

            Reversible (Unarchive exists), so it is NOT destructive and keeps the
            neutral treatment — it sits above the destructive item, never among them.
          */
          // Derived from `archived_at`; `row.archived` is a phantom field that is
          // always undefined, which made an archived certificate's menu offer
          // "Archive" again and left Unarchive unreachable from this page.
          label: row.archived_at ? 'Unarchive' : 'Archive',
          onClick: () => handleToggleArchive(row),
          visible: true,
          // Category 3 (in-flight mutation) — disabled, never hidden, and the tooltip must
          // say why: an unexplained disabled row is exactly the silent state this rule bans.
          disabled: isArchiving || isUnarchiving,
          tooltip: isArchiving
            ? 'Archiving…'
            : isUnarchiving
              ? 'Unarchiving…'
              : undefined,
          divider: true,
          icon: <ArchiveIcon strokeWidth={1} />,
        },
        {
          // VRG-231 review — a group PARENT's delete goes through the
          // dedicated `DELETE /certificates/admin/groups/:id` route (see
          // DeleteCertificateModal), which requires manage_certificate_groups
          // — a DIFFERENT permission than the plain single-certificate delete
          // this trigger otherwise gates on (transfer_certificate). Without
          // this, a user with transfer_certificate but not
          // manage_certificate_groups could open the modal and hit a 403.
          // Disable+tooltip (§27, in-context action), scoped to group
          // parents only — a non-group certificate's delete gating is
          // unchanged.
          label: 'Delete',
          onClick: () => handleDeleteCertificate(row),
          // §24: gates on `certificate_delete`. A group PARENT additionally deletes via the
          // group-delete route, which requires `manage_certificate_groups`; both are PERMISSION
          // blocks, so both disable+tooltip rather than hide (§46).
          disabled: !canDeleteCertificate || (isGroupParentRow && !isCanManageGroups),
          tooltip: !canDeleteCertificate
            ? "You need the 'Delete certificates' permission. Ask an admin to grant it."
            : isGroupParentRow && !isCanManageGroups
            ? "You need the 'Manage Certificate Groups' permission. Ask an admin to grant it."
            : undefined,
          visible: isVisibleDelete,
          destructive: true,
          divider: true,
          icon: <TrashIcon strokeWidth={1} color={theme.palette.error.main} />
        },
      ]
    },
    [
      isCanTransfer,
      isCanManageGroups,
      groupRole,
      groupData,
      certificate?.transfer?.type,
      canReplaceTag,
      canActivateCertificate,
      canDeleteCertificate,
      isInternalUser,
      certificate?.transfer?.type,
      mutationState.isActivating,
      theme,
      primaryInstallation,
      notify,
      isPrimaryLoading,
      isPrimaryError,
      refetchPrimaryInstallation,
      certificate,
      isArchiving,
      isUnarchiving,
      isReleasingHold,
      // VRG-491 — `unacknowledgedInvalidScans`, `canAcknowledgeInvalidScans` and
      // `isAcknowledging` were listed here for the acknowledge ROW. That row is gone (see
      // the note where it stood), and all three are still live elsewhere on this page —
      // the popover and the confirm modal — so they are dropped from THIS dependency list
      // only, not from the component.
    ]
  )

  // VRG-234 — row kebab for a "Children"/"Parent"/"Related" member card. VRG-231
  // review item 5: this must offer the FULL certificate action set (not a reduced
  // one), using the canonical labels the rest of the app already uses (CertificatesPage's
  // "Replace Tag", not the previous mislabeled "Edit"), plus "Remove from group" —
  // reuses the same modal state + handlers as the header actions (they already
  // accept any certificate, not just the page's own).
  const getGroupMemberActionItems = useCallback(
    (row: CertificateAdmin): ActionDropdownItem[] => {
      // VRG-231 review round 2 item 12 — `row` here can itself be the group's
      // PARENT (rendered in a child's own "Parent" section), and a parent is
      // tagless, so it never offers "Replace tag" either.
      const isRowGroupParent = Boolean(row.group_divisibility) && !row.parent_certificate_id
      // ⚠️ VRG-231 round 5 — Transfer was `=== 'active'` here: INVERTED, and the
      // inversion made it unreachable under the product's DEFAULT activation
      // trigger (ON_PRIMARY_TRANSFER — "certificates activate when the customer
      // claims ownership"), where the transfer is WHAT ACTIVATES. Requiring active
      // first is circular. All predicates now come from utils/certificate, so this
      // kebab and the same certificate's row on the list cannot answer differently.
      const isVisibleReplaceTag = isReplaceTagAvailable(row.status, isRowGroupParent)
      const isVisibleTransfer = isTransferAvailable(row.status)
      const isInTransferState = isCertificateInTransferState(row.status)
      const isVisibleDelete = isDeleteAvailable(row)
      // VRG-231 review round 3 — ADDED. This menu was missing Activate entirely,
      // so a member row offered strictly less than the SAME certificate's row on
      // the certificates list, which offers it on exactly this gate. Being in a
      // group is not a reason to lose an action the record otherwise has.
      //
      // Same predicate as CertificatesPage's own `isVisibleActivate`, deliberately
      // — one rule for when a certificate can be activated, not a group-local
      // variant that drifts from it.
      //
      // NOT restricted to divisible groups. Whether an INDIVISIBLE group's member
      // should be individually activatable is a real product/billing question, but
      // it is pre-existing rather than introduced here: the group PARENT's own
      // kebab already offers Activate on an indivisible group today. Raising it
      // rather than silently answering it either way.
      const isVisibleActivate = isActivateAvailable(row.status)

      return [
        {
          label: 'Preview',
          onClick: () => handlePreviewCertificate(row),
          visible: true,
          icon: <EyeIcon strokeWidth={1} />,
        },
        {
          label: 'Details',
          onClick: () => navigate(ROUTES.CERTIFICATE(row.id), { state: groupMemberReferrer }),
          visible: true,
          icon: <InfoIcon strokeWidth={1} />,
        },
        {
          label: 'Replace tag',
          onClick: () => handleReplaceTagModal(row),
          visible: isVisibleReplaceTag,
          icon: <ReplaceIcon strokeWidth={1} />,
        },
        {
          label: 'Activate',
          onClick: () => handleActivateCertificate(row),
          visible: isVisibleActivate,
          // Two independent disable reasons (permission + in-flight), so the tooltip
          // must name whichever is actually active — §46 forbids a silently disabled
          // item. Mirrors the header's own Activate exactly.
          disabled: !canActivateCertificate || mutationState.isActivating,
          tooltip: !canActivateCertificate
            ? "You need the 'Activate certificate' permission. Ask an admin to grant it."
            : mutationState.isActivating
              ? 'Activating…'
              : undefined,
          icon: <BadgeCheckIcon strokeWidth={1} />,
        },
        {
          label: 'Transfer',
          onClick: () => handleTransferCertificate(row),
          visible: (isVisibleTransfer || isInTransferState) && isCanTransfer,
          disabled: isInTransferState,
          icon: <ArrowLeftRightIcon strokeWidth={1} />,
        },
        {
          // VRG-231 review round 2 item 5 — "Remove from group", reversing the
          // Aug-7 "Split from group" rename (see handleRemoveFromGroup's own
          // comment for why). Severs this member's link only — the certificate
          // itself survives, so Unlink (not Trash) per §38. Only ever offered on
          // a CHILD row (`parent_certificate_id` set) — the unlink endpoint
          // validates "is this a child of a group", so showing it on the PARENT
          // row (rendered in a child's own "Parent" section) would 400.
          //
          // VRG-231 review round 2 item 3 — SUPERSEDES the old "divisible only"
          // gate: an indivisible group's child can ALSO leave the group while
          // every member (parent + children) is still not-yet-live and unowned
          // — see `canSplitCertificateGroup` (utils/certificate.ts), which
          // mirrors the server's `assertIndivisibleGroupIsSplittable`.
          // `groupData` (fetched once at the top of this page, keyed off the
          // CURRENT certificate) describes this exact group regardless of
          // whether `row` here is a Children/Parent/Related member — all three
          // sections render members of the SAME group as the page's own
          // certificate — so no extra fetch is needed.
          label: 'Remove from group',
          onClick: () => handleRemoveFromGroup(row),
          visible: Boolean(row.parent_certificate_id) && canSplitCertificateGroup(row.group_divisibility, groupData) && isCanManageGroups,
          disabled: isUnlinking,
          icon: <UnlinkIcon strokeWidth={1} />,
        },
        {
          // Same gating as the header's own Delete above — a group PARENT row
          // (rendered here when a child's "Parent" section shows the parent
          // itself) deletes via the group-delete route, which requires
          // manage_certificate_groups.
          label: 'Delete',
          onClick: () => handleDeleteCertificate(row),
          disabled: isRowGroupParent && !isCanManageGroups,
          tooltip: isRowGroupParent && !isCanManageGroups
            ? "You need the 'Manage Certificate Groups' permission. Ask an admin to grant it."
            : undefined,
          visible: isVisibleDelete && isCanTransfer,
          destructive: true,
          divider: true,
          icon: <TrashIcon strokeWidth={1} color={theme.palette.error.main} />,
        },
      ]
    },
    // `canActivateCertificate` + `mutationState.isActivating` added with the Activate
    // row above — a stale closure here would leave the item enabled after the
    // permission or the in-flight state changed.
    [isCanTransfer, isCanManageGroups, isUnlinking, theme, groupMemberReferrer, navigate, groupData, canActivateCertificate, mutationState.isActivating, isDemoCompany, isConvertingDemoCertificate],
  )

  return (
    <>
      {isLoading ? (
        // Footprint-parity placeholder (§19) in place of the centred <Loader />, which reserved none
        // of the 122px header / 53px tab row / 596px panel. See CertificatePageSkeleton.
        <CertificatePageSkeleton certificateId={id as string} />
      ) : isEmpty ? (
        <NotFoundPage />
      ) : (
        <>
          <Header
            // No `sx={{ pt: 1 }}` — Header positions its first row on the sidebar logo's centre
            // line itself, so a per-page nudge only pushes this one page 4px off that datum.
            breadcrumbs={true}
            // breadcrumbsOveride gates the Header's breadcrumb render; certificateProduct
            // makes Breadcrumbs render "Certificates > <product>" (dropping /scans etc.).
            breadcrumbsOveride={certificate?.product.title}
            certificateProduct={{ name: certificate?.product.title ?? '' }}
            title={certificate?.product.title}
            // VRG-361. Reads the CERTIFICATE's flag, not the product's, even though the
            // title is the product's name: this page is the certificate, and a demo
            // certificate is the row that must never bill.
            titleAdornment={<ExampleKindBadge kind={certificate?.example_kind} />}
            status={{
              statuses: CERTIFICATE_STATUSES,
              value: certificate?.status,
            }}
            /* Three signals share the status band, all rendering after the StatusChip via
               `leftComponents` — so this needs no change to `Header` itself.

               VRG-234 — the group-type glyph (parent = multi-component, child = single
               tagged item within a group). Absent on a standalone certificate
               (groupRole undefined): no glyph, no chip, nothing.

               VRG-491 — the two integrity signals, here because this band is where a
               reader looks to find out what condition this certificate is in. Neither
               existed on this page before: the tamper state was reachable only by opening
               the Scans tab and reading a chip on a row, and the invalid count was nowhere
               at all (the banner below says an invalid scan happened, never how many).

               The integrity pair are deliberately DIFFERENT shapes. "Opened" is a state the
               object is in and so is a badge; the invalid tally is a count of events and so
               is a glyph plus a number. A badge reading "3 invalid scans" would assert the
               certificate is invalid, which it is not — three taps failed, and possibly not
               by anyone who has ever held the item. */
            leftComponents={
              <Stack direction="row" spacing={3} alignItems="center">
                {groupRole && (
                  <CertificateGroupRoleIcon
                    role={groupRole}
                    status={certificate?.status}
                    divisibility={certificate?.group_divisibility}
                  />
                )}
                {/* `true` only. `false` and NULL both render nothing — most tags carry no
                    tamper loop at all (93% of scans locally are NULL), and drawing them as
                    "intact" would claim a physical guarantee nobody checked. */}
                <TamperBadge tampered={certificate?.is_tag_tampered} />
                {/* VRG-491 — the indicator is a CONTROL here, not just a readout: clicking
                    it opens a small panel carrying the count in words, the way through to
                    the rows it counted, and Acknowledge. Rationale for popover-over-tooltip,
                    and for keeping the kebab row as well, lives in `InvalidScanPopover`.

                    Rendered only once the count has actually arrived. `certificate` is
                    needed for the link's route, and `InvalidScanPopover` returns null on a
                    zero/absent count of its own accord, so no guard is duplicated here. */}
                {certificate && (
                  <InvalidScanPopover
                    size={16}
                    /* VRG-528 — read off `certificate` directly. This deliberately reverses
                       the VRG-491 note that used to sit here saying the count could NOT come
                       from this payload: that was true only while the page loaded through the
                       OptionalAuth public endpoint. It now loads the admin projection, which
                       is where the count was all along. */
                    count={certificate.invalid_scan_count}
                    reasonCounts={certificate.invalid_reason_counts}
                    // Leads to the rows it counted: the Scans tab, pre-filtered to invalid.
                    // `Scans` seeds its filter model from this param, so the filter lands in
                    // the Filters drawer as an ordinary applied filter and clears like one.
                    // This was the indicator's own click target before the popover existed;
                    // it moved in here rather than being dropped.
                    viewScansTo={`${ROUTES.CERTIFICATE_SCANS(certificate.id)}?scan_type=invalid`}
                    // Same modal the kebab row opens — one confirmation, two entry points.
                    onAcknowledge={() => setOpenAcknowledgeModal(true)}
                    canAcknowledge={canAcknowledgeInvalidScans}
                    isAcknowledging={isAcknowledging}
                  />
                )}
              </Stack>
            }
            // VRG-231 review item 1 — buttons-plus-overflow, matching the
            // established platform convention (CompanyPage.tsx on origin/main: a
            // Stack of Buttons + an ActionDropdown "button" kebab), NOT a single
            // "Actions ▾" dropdown. View / Activate / Archive stay full buttons
            // (exactly as origin/main had them); everything else collapses into
            // the kebab via getActionItems. Figma (1162-360153 / 1339-156825)
            // shows only a dropdown here, but that contradicts the established
            // convention — direct product-owner override, 2026-08-07.
            // VRG-231 review round 2 item 1 — spacing={3} (12px), matching
            // origin/main's CertificatePage header (verified via `git show
            // origin/main:...CertificatePage.tsx`) rather than working-conventions
            // §29's spacing={4} for header groups — main has since moved to 3 here
            // and main wins. Flagging the doc/code discrepancy rather than silently
            // resolving it.
            rightComponents={
              <Stack direction="row" spacing={3} alignItems="center">
                {/* Two genuinely different destinations, so both exist and the labels say
                    which is which. "View live" opens the brand's own site. "Preview" opens a
                    drawer rendering the customer app's real preview — no activation (which is
                    billable) and no dependency on an installation.
                    Ordered utility-first per working-conventions §33. */}

                {/* VISIBILITY is record state, so it HIDES (§46).
                    Since VRG-319 a `created` or `inactive` certificate does not verify
                    publicly at all — the public path redirects to `/certificate-not-active`
                    (see the `certificate_not_activated` branch in
                    `verify.public.controller.ts`). So on those two statuses this button would
                    open a page telling the brand their own certificate is not active, which
                    is worse than not offering it. `isViewLiveAvailable` is deliberately the
                    complement of `isActivateAvailable` over the same statuses, which is also
                    what discharges §46's obligation that hiding an action must leave the
                    RESOLVING action reachable: Activate is in this same header, visible on
                    exactly the status set that hides this.

                    DISABLEDNESS is a different question with a different answer. Having no
                    primary installation is resolvable by the user — but in Settings, not
                    here — so hiding would strand them with no way to find the fix, and this
                    stays visible with a tooltip naming it. A disabled MUI Button swallows
                    pointer events, hence the span. */}
                {isViewLiveAvailable(certificate?.status) && (
                  <Tooltip
                    title={
                      primaryInstallation
                        ? ''
                        : 'Set up an installation to view this certificate on your site.'
                    }
                  >
                    <span>
                      <Button
                        variant="secondary"
                        disabled={!primaryInstallation}
                        startIcon={<ArrowUpRightIcon size={16} strokeWidth={1} />}
                        onClick={() => certificate && handlePreviewCertificate(certificate)}
                      >
                        View live
                      </Button>
                    </span>
                  </Tooltip>
                )}

                <Button
                  variant="secondary"
                  startIcon={<EyeIcon size={16} strokeWidth={1} />}
                  onClick={() => setOpenPreview(true)}
                >
                  Preview
                </Button>
                {/* Replace tag. Available at ANY status (VRG-268) — the case the feature
                    exists for is a damaged or lost tag on an item that has already been
                    sold, so gating it on `inactive` would exclude it.

                    §46: this is a PERMISSION gate, so the button stays visible and goes
                    disabled with a tooltip naming what to ask for, rather than vanishing
                    for anyone without the grant. A disabled MUI Button swallows pointer
                    events, hence the span — same shape as "View live" above. */}
                <Tooltip
                  title={
                    canReplaceTag
                      ? ''
                      : "You need the 'Replace tag' permission. Ask an admin to grant it."
                  }
                >
                  <span>
                    <Button
                      variant="secondary"
                      disabled={!canReplaceTag}
                      startIcon={
                        certificateHasTag ? (
                          <ReplaceIcon size={16} strokeWidth={1} />
                        ) : (
                          <TagIcon size={16} strokeWidth={1} />
                        )
                      }
                      onClick={() => certificate && handleReplaceTagModal(certificate)}
                    >
                      {/* VRG-388 — ONE control, label follows record state (§46). A tagless
                          certificate is not an error to gate out of this flow; it is the ADD
                          case, and it is how a link-only demo becomes a physical Tier-1 send.
                          Same permission either way, so this is NOT an access gate and the
                          §46 disable+tooltip above is unaffected. */}
                      {certificateHasTag ? 'Replace tag' : 'Add tag'}
                    </Button>
                  </span>
                </Tooltip>
                {isActivateAvailable(certificate?.status) && (
                  <>
                {/* Activate is the PRIMARY action, and it is LAST — the rightmost button in
                    the group, per §33/§26. It previously sat before "Replace tag" as a
                    secondary, on a lifecycle-ordering argument ("activate, then replace").
                    That argument does not survive VRG-319: an unactivated certificate now
                    refuses to verify publicly, so on exactly the certificates where this
                    button appears, activating it is not one reasonable next step among
                    several, it is the ONE thing standing between the item and working at
                    all. §33's rule is one primary per header, rightmost and filled; a
                    lifecycle reading order is not a reason to demote the page's main action
                    to look like Preview and Replace tag.

                    Last among the BUTTONS, not after the kebab: the ActionDropdown is
                    overflow chrome rather than an action button, and it stays anchored at
                    the end of the row on every page in the app.

                    It disappears once the certificate is active — that is a RECORD-STATE
                    block, which §46 says to HIDE (there is nothing to explain, and the
                    resolving action is the one that just happened). The permission and the
                    in-flight mutation are the other two §46 categories and both DISABLE
                    with a tooltip naming whichever is actually in force. `certificate_activate`
                    is held by platform_admin + company_admin, so for anyone else there is a
                    real admin who can grant it — which is precisely why it is disabled here
                    rather than hidden. */}
                <Tooltip
                  title={
                    !canActivateCertificate
                      ? "You need the 'Activate certificate' permission. Ask an admin to grant it."
                      : mutationState.isActivating
                        ? 'Activating…'
                        : ''
                  }
                >
                  <span>
                    <Button
                      variant="primary"
                      startIcon={<BadgeCheckIcon size={16} strokeWidth={1} />}
                      onClick={() => certificate && handleActivateCertificate(certificate)}
                      disabled={!canActivateCertificate || mutationState.isActivating}
                    >
                      Activate
                    </Button>
                  </span>
                </Tooltip>
                  </>
                )}
                {/* PAST HERE IT IS A KEBAB, and that is the rule rather than a preference.
                    A detail page keeps at most THREE inline action buttons; the fourth
                    collapses the overflow into an actions dropdown (the kebab-parity rule —
                    the threshold is MORE than three, so three inline is fine).

                    Inline: Preview, Replace tag, and Activate (only on an inactive/created
                    certificate, and the primary). Menu: View live, Transfer, Cancel transfer, Mark as received
                    (VRG-275, only while a held replacement exists), Archive/Unarchive, Delete.
                    Activate being conditional is what sets the ceiling — it appears exactly on
                    the certificates where a fourth button would tip the row over.

                    This dropdown is also the fix for a page that had been rendering NOTHING
                    from it. `ActionDropdown` was imported and `getActionItems` was defined, and
                    neither was ever called — so Transfer, Cancel transfer and Delete had no
                    trigger anywhere on this page, and the parity the row kebab promises was
                    silently broken. Nothing appears both here and as a button above. */}
                <Box data-tour="certificate-detail-transfer">
                  {/* `variant="button"`: secondary-button chrome, matching the header actions
                      it sits between — "Add tag"/"Replace tag" on its left and the Ask
                      launcher on its right, both bordered 40px secondaries. Without it the
                      kebab falls through to a bare `IconButton` with no border, so it reads as
                      the one unstyled control in the row. `CompanyPage` was already passing
                      this; the certificate header was not. */}
                  <ActionDropdown items={getActionItems(certificate ?? null)} variant="button" />
                </Box>
              </Stack>
            } />

          {/* Held Replacement Alert — VRG-275: a replacement posted to the owner is bound
              but HELD, not yet resolving, until they confirm receipt (or a brand releases
              it below via "Mark as received"). The old tag is already retired by this
              point, so the certificate is genuinely unscannable right now — the brand needs
              to see that here, not discover it from a support ticket. Reuses the shared
              Alert like the suspicious-activity banner below. No dismiss: this describes
              live state rather than a one-off notice, and it disappears on its own once the
              hold clears. */}
          {certificate?.held_replacement && (
            <Alert
              severity="warning"
              icon={<PackageIcon size={20} strokeWidth={1.75} />}
              title="Replacement tag not yet active"
              description="This certificate's tag stopped working when the replacement was sent. It won't be scannable again until the owner confirms they've received the new tag."
              sx={{ my: 4 }}
            />
          )}

          {/* Suspicious Activity Alert — VERA-415 / VERA-908: an invalid scan on this
              certificate is possible counterfeit activity, so it's raised at the top of
              the page rather than left as a chip buried in the Scans tab. Reuses the
              shared @repo/ui Alert (working-conventions §28/§36) instead of a hand-rolled
              Box. Copy is deliberately short — Figma's four-line draft reads as a wall of
              text at exactly the moment the reader wants to know what's wrong and where to
              look (mirrors the equivalent apps/app banner). No "Learn more" link: there is
              no dedicated help-center article for suspicious-scan activity to link to
              today (grepped for one — only a general Help Center page exists). Dismiss is
              per-visit local state (showSuspiciousAlert), not persisted. */}
          {certificate?.has_invalid_scans && showSuspiciousAlert && (
            <Alert
              severity="error"
              icon={<ShieldAlert size={20} strokeWidth={1.75} />}
              title="Suspicious activity detected"
              description={
                invalidScanLocation
                  ? `An invalid scan was recorded at ${invalidScanLocation}. Review the Scans tab for details.`
                  : 'An invalid scan was recorded. Review the Scans tab for details.'
              }
              onClose={() => setShowSuspiciousAlert(false)}
              sx={{ my: 4 }}
            />
          )}

          {/* NO TAMPER ALERT — deliberately, and this is the only place that decision is
              visible, so it is recorded here rather than left as an absence.

              A red "Tag tampering detected" banner used to render here alongside the neutral
              grey "Opened" pill in the status band above, which made the page say two
              contradictory things about one signal: the pill called it a fact, the banner
              called it an error. The banner won by loudness and was wrong.

              What the hardware actually reports is that the NTAG 424 TT loop is broken. It
              cannot distinguish "the customer opened the package they bought" — the
              overwhelmingly common cause, and on a closure/seal tag the EXPECTED one — from
              "someone opened it to swap the contents". Asserting "physically tampered with"
              overclaims, and the copy went further still by telling a brand to verify the
              product "before relying on this certificate" on the strength of an owner
              unwrapping their own purchase.

              Same error class as `invalid_key`, and resolved the same way: see the long note
              in `utils/invalidScanSeverity.ts` on why red is reserved for what we can prove.
              Spend it on ambiguity and it stops being read anywhere it does mean act-now.

              So the pill is the whole signal, and its glossary tooltip carries the nuance a
              banner could not (working-conventions §44). Only INVALID SCANS raise a red alert
              on this page — the `has_invalid_scans` banner directly above, unchanged. Do not
              reintroduce a tamper banner here. */}

          {certificate && (
            <Box>
              <Tabs tabs={[{
                label: 'Product',
                value: ROUTES.CERTIFICATE(certificate.id),
              }, {
                label: 'Provenance',
                value: ROUTES.CERTIFICATE_PROVENANCE(certificate.id),
              }, {
                label: 'Scans',
                value: ROUTES.CERTIFICATE_SCANS(certificate.id),
              },
              {
                label: 'Activity',
                value: ROUTES.CERTIFICATE_ACTIVITY(certificate.id),
              }]}>
                <TabPanel sx={{ p: 0, pt: 6 }} value={ROUTES.CERTIFICATE(certificate.id)}>
                  <Product
                    certificate={certificate}
                    refetch={refetch}
                    groupRole={groupRole}
                    groupData={groupData}
                    isGroupLoading={isGroupLoading}
                    getGroupMemberActionItems={getGroupMemberActionItems}
                  />
                </TabPanel>
                <TabPanel data-tour="certificate-detail-provenance" sx={{ p: 0, pt: 6 }} value={ROUTES.CERTIFICATE_PROVENANCE(certificate.id)}>
                  <Provenance />
                </TabPanel>
                <TabPanel sx={{ p: 0, pt: 6 }} value={ROUTES.CERTIFICATE_SCANS(certificate.id)}>
                  <Scans certificate={certificate} />
                </TabPanel>
                <TabPanel data-tour="certificate-detail-activity" sx={{ p: 0, pt: 6 }} value={ROUTES.CERTIFICATE_ACTIVITY(certificate.id)}>
                  <ActivityTab certificateId={certificate.id} />
                </TabPanel>
              </Tabs>
            </Box>
          )}
        </>
      )}

      {/* Modals */}
      {/*
        VRG-836 — "Convert to real". The gate that names the COST: this records the
        activation, so it appears on the next invoice, and `sample_data_manage`'s own
        description ("samples are never billed") does not say so. Rendered outside the
        `selectedCertificate` guard below for the same reason the arrival gate is — it is
        driven by the page's own certificate, not by a selected row.
      */}
      <ConvertDemoRowModal
        kind="certificate"
        open={openConvertDemoModal}
        onClose={() => setOpenConvertDemoModal(false)}
        onConfirm={handleConfirmConvertDemo}
        loading={isConvertingDemoCertificate}
        name={certificate?.product?.title}
        error={convertDemoCertificateError}
      />
      {/*
        Rendered OUTSIDE the `selectedCertificate` guard below. That guard exists for
        row actions, which set a selected row first; this modal is driven by the
        page's own certificate, so gating it there left it mounted never — the kebab
        item fired, state flipped, and nothing appeared.
      */}
      {/*
        VERA-931 — the gate on confirming arrival. Under AT_STORE this is the moment
        the certificate activates and the company is charged, so it takes the same
        modal every other activation change takes rather than firing straight off a
        menu click. `resolvedActivatesOnArrival` decides the wording: for a brand on
        On sale, arrival genuinely costs nothing, and warning them anyway is the
        noise that teaches people to ignore the warning that matters.
      */}
      <ConfirmSettingModal
        open={openConfirmArrival}
        onClose={() => setOpenConfirmArrival(false)}
        onConfirm={handleConfirmArrival}
        title="Confirm this arrived?"
        consequence={
          resolvedActivatesOnArrival
            ? 'This certificate activates on arrival, and activation is what makes it billable. Confirming here starts the charge for it. It also adds the location to the certificate’s chain of custody, permanently.'
            : 'This adds the location to the certificate’s chain of custody, permanently. It does not activate or charge the certificate: this one activates at a different point.'
        }
        confirmLabel="Confirm arrival"
        loading={custodyState.isReceiving}
      />

      <SendToLocationModal
        open={openSendToLocation}
        onClose={() => setOpenSendToLocation(false)}
        certificateId={id as string}
        resolvedTrigger={
          certificate?.activation_trigger ??
          certificate?.product?.activation_trigger ??
          null
        }
        loading={custodyState.isDispatching}
        onSend={addressId =>
          custodyState.sendToStore({ certificateId: id as string, addressId })
        }
      />

      {selectedCertificate && (
        <>
          <TransferCertificateModal
            open={openTransferModal}
            onClose={handleCloseTransferModal}
            certificate={selectedCertificate}
            refetch={refetch}
          />
          <CancelTransferModal
            open={openCancelTransferModal}
            onClose={handleCloseCancelTransferModal}
            certificate={selectedCertificate}
          />

          <ReplaceTagModal
            open={openReplaceTagModal}
            onClose={handleCloseReplaceTagModal}
            certificate={selectedCertificate}
            refetch={refetch}
          />
          <DeleteCertificateModal
            open={openDeleteModal}
            onClose={handleCloseDeleteModal}
            certificate={selectedCertificate}
            refetch={handleDeleted}
          />
          {/* VRG-275 — "Mark as received", the operational escape hatch for a HELD
              replacement. This is the brand VOUCHING that the owner has the tag, not the
              owner's own confirmation — the normal path is the owner signing in and
              confirming receipt themselves. The copy says so explicitly rather than
              reading like an equivalent, faster alternative. */}
          <ConfirmSettingModal
            open={openReleaseHoldModal}
            onClose={handleCloseReleaseHoldModal}
            onConfirm={handleConfirmReleaseHold}
            title="Mark the replacement tag as received?"
            consequence="This activates the replacement now, on your word that the owner has received it — not theirs. It's the fallback for when the owner can't confirm receipt themselves; normally they do that by signing in and scanning the new tag."
            confirmLabel="Mark as received"
            loading={isReleasingHold}
          />
        </>
      )}

      <SplitGroupModal
        open={openSplitGroupModal}
        onClose={handleCloseSplitGroupModal}
        certificate={certificate ?? null}
        children={groupData?.children ?? []}
        onSplit={handleSplitGroupSuccess}
      />

      {/* VRG-231 review round 2 item 5 — gates BEFORE applying (§43): only reachable when
          removing pendingRemoveFromGroupRow would leave the group with NO children,
          which UnlinkCertificatesFromGroupUseCase expands into dissolving the whole
          group and archiving the parent. See handleRemoveFromGroup's own comment.
          Round 3: the threshold moved from "one would remain" to "none would" — a
          one-child group is now legitimate, so the old copy (which reasoned from "a
          group of one isn't a group") described a rule that no longer exists. */}
      <ConfirmSettingModal
        open={Boolean(pendingRemoveFromGroupRow)}
        onClose={handleCloseRemoveFromGroupModal}
        onConfirm={handleConfirmRemoveFromGroup}
        title="Remove the group's last member?"
        consequence="This is the only certificate left in the group. Removing it dissolves the group, and the tagless parent certificate is archived."
        confirmLabel="Remove and dissolve group"
        loading={isUnlinking}
      />

      <CreateCertificateGroupModal
        open={openCreateGroupModal}
        onClose={handleCloseCreateGroupModal}
        initialCertificate={certificate ?? null}
        onCreated={() => {
          refetch()
          refetchGroup()
        }}
      />

      {/* VRG-491 — acknowledging invalid scans.

          WHY THIS IS CONFIRMED AT ALL (§43 decides Save-vs-confirm on consequence, not on
          how many fields the input has). The action has one click and no invalid
          intermediate state, so it needs no Save. It earns a CONFIRM for the other reason:
          the effect is downstream and the window it opens cannot be recovered. Between an
          accidental acknowledgement and the next invalid scan, a live counterfeit signal is
          invisible to everyone in the company — and nobody can tell that happened by looking
          at the certificate, because a cleared indicator is indistinguishable from one that
          never fired.

          The modal's real job here is to STATE THE RE-RAISE RULE, which is the half a button
          structurally cannot say (§43). "Acknowledge" alone gives the operator no way to know
          whether they are silencing this certificate once or forever — and those are very
          different acts. Naming it is also what makes the action safe to take: knowing it
          re-raises is precisely what makes acknowledging reasonable rather than reckless.

          Not `destructive` (§32): nothing ceases to exist. Every invalid scan stays in the
          Scans tab, so the confirm is a consequence gate, not a delete. */}
      <ConfirmSettingModal
        open={openAcknowledgeModal}
        onClose={() => setOpenAcknowledgeModal(false)}
        onConfirm={() => certificate && acknowledgeInvalidScans(certificate.id)}
        title={
          unacknowledgedInvalidScans === 1
            ? 'Mark this invalid scan as reviewed?'
            : `Mark these ${unacknowledgedInvalidScans} invalid scans as reviewed?`
        }
        consequence={
          <>
            The invalid-scan indicator clears on this certificate and in the certificates
            table, for everyone in your company.{' '}
            <strong>Any invalid scan recorded from now on will raise it again.</strong> The
            scans themselves are kept and stay listed in the Scans tab.
          </>
        }
        confirmLabel="Mark as reviewed"
        loading={isAcknowledging}
        // §46 — the confirm is an action, so a caller lacking the grant sees it disabled
        // with the permission named, never hidden. Reachable in practice: the menu row can
        // be opened by keyboard before a permissions refetch settles.
        confirmDisabled={!canAcknowledgeInvalidScans}
        confirmTooltip={
          canAcknowledgeInvalidScans
            ? undefined
            : "You need the 'Acknowledge scans' permission. Ask an admin to grant it."
        }
      />

      <Modal
        title="Archive certificate"
        open={openArchiveModal}
        onClose={handleCloseArchiveModal}
        actions={
          <>
            <Button
              disableElevation
              variant="secondary"
              onClick={handleCloseArchiveModal}
              disabled={isArchiving}
            >
              Cancel
            </Button>
            <Button
              disableElevation
              onClick={handleConfirmArchive}
              loading={isArchiving}
              disabled={isArchiving}
            >
              Archive
            </Button>
          </>
        }
      >
        <Typography variant="body1" sx={{ fontSize: 16, fontWeight: 400, lineHeight: 1.5 }}>
          Archive this certificate? It will be hidden from the default view but can be restored.
        </Typography>
      </Modal>

      {/* VRG-231 — see CertificatesPage: this was the second, already-drifted
          copy of the same hand-rolled modal (it keyed its group sentence off
          `groupRole`, the list's off a local `isGroupParentRow`). Both now
          render one shared component that computes the real cascade count. */}
      <ActivateCertificateModal
        certificate={certificate}
        open={openActivateModal}
        onClose={handleCloseActivateModal}
        onConfirm={handleConfirmActivate}
        isLoading={mutationState.isActivating}
      />

      <CertificatePreviewDrawer
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        certificateId={id as string}
        title={certificate?.product?.title}
      />
    </>
  )
}
